import { TwinType } from '@api/twin/TwinApi';
import { TeiaViewerSourceType, useTeiaViewerContext, ViewerSignal } from '@stereograph/teiaviewer';
import { createContext, ReactNode, useContext, useMemo } from 'react';

export interface AnomalyWidgetContext {
  projectId: number;
  twinProjectType: TwinType;
  onAnomalyCreationSignal: ViewerSignal<() => void>;
  onOpenAnomalyListSignal: ViewerSignal<() => void>;
}

export interface AnomalyWidgetContextProviderProps {
  projectId: number;
  children: ReactNode;
  onAnomalyCreationSignal: ViewerSignal<() => void>;
  onOpenAnomalyListSignal: ViewerSignal<() => void>;
}

const Context = createContext<AnomalyWidgetContext | null>(null);

export const AnomalyWidgetContextProvider = (props: AnomalyWidgetContextProviderProps) => {
  const { projectId, onAnomalyCreationSignal, onOpenAnomalyListSignal, children } = props;
  const { viewer } = useTeiaViewerContext();

  const twinProjectType: TwinType
    = viewer.currentSource?.sourceType === TeiaViewerSourceType.Twin
      ? viewer.currentSource?.type
      : 'Draft';

  const context = useMemo(
    () => ({
      projectId,
      twinProjectType,
      onAnomalyCreationSignal,
      onOpenAnomalyListSignal,
    }),
    [
      projectId,
      twinProjectType,
      onAnomalyCreationSignal,
      onOpenAnomalyListSignal,
    ],
  );

  return <Context.Provider value={context}>{children}</Context.Provider>;
};

export const useAnomalyWidgetContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error('Anomaly widget context not found');
  }

  return context;
};
